// Türkçe etiketler — BE audit action / entity adlarını CMS yüzüne çevirir.
//
// BE her aksiyon için kısa, nokta-ayraçlı bir dize gönderir ("vehicle.create",
// "article.publish" vb.). Burada bildiğimiz aksiyonlar için açıklayıcı bir
// Türkçe karşılık veriyoruz; bilinmeyen aksiyonlar için ham aksiyon dizesi
// tag içinde gösterilir.

import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import auditTr from '@/i18n/locales/tr/audit.json';

const tt = i18n.t.bind(i18n) as (k: string, o?: Record<string, unknown>) => string;

/**
 * Stable BE-key value lists. Derived from the `audit` namespace JSON so the
 * filter dropdowns (and any callers needing the known action/entity keys) stay
 * data-driven; the Turkish copy itself lives only in the i18n resources.
 */
export const ACTION_KEYS = Object.keys(auditTr.actionLabels);
export const ENTITY_KEYS = Object.keys(auditTr.entityLabels);

export function labelAction(action: string | null | undefined): string {
  if (!action) return '—';
  return tt(`audit:actionLabels.${action}`, { defaultValue: action });
}

export function labelEntity(entityType: string | null | undefined): string {
  if (!entityType) return '—';
  return tt(`audit:entityLabels.${entityType}`, { defaultValue: entityType });
}

/**
 * i18n-backed resolvers for the audit LOG PAGE UI. Mirrors `labelAction` /
 * `labelEntity` but reads the Turkish copy from the `audit` namespace so no
 * user-facing string is hardcoded in the page. Falls back to the raw BE key
 * when no translation exists (unknown actions/entities).
 *
 * The plain `labelAction` / `labelEntity` above stay non-hook because
 * `src/api/auditLog.ts` (a cross-feature, non-React module) builds the legacy
 * `summary` field from the static maps.
 */
export function useActionLabel(): (action: string | null | undefined) => string {
  const { t } = useTranslation('audit');
  const td = t as unknown as (key: string, options?: Record<string, unknown>) => string;
  return (action) => {
    if (!action) return '—';
    return td(`actionLabels.${action}`, { defaultValue: action });
  };
}

export function useEntityLabel(): (entityType: string | null | undefined) => string {
  const { t } = useTranslation('audit');
  const td = t as unknown as (key: string, options?: Record<string, unknown>) => string;
  return (entityType) => {
    if (!entityType) return '—';
    return td(`entityLabels.${entityType}`, { defaultValue: entityType });
  };
}

export function actionColor(action: string | null | undefined): string {
  if (!action) return 'default';
  if (action.includes('delete') || action.includes('reject') || action.includes('ban')) return 'red';
  if (action.includes('publish') || action.includes('approve') || action.includes('activate')) return 'green';
  if (action.includes('create') || action.includes('add')) return 'blue';
  if (action.includes('update') || action.includes('change')) return 'cyan';
  if (action.includes('archive') || action.includes('hide') || action.includes('rotate')) return 'orange';
  if (action.includes('login') || action.includes('logout')) return 'purple';
  if (action.includes('export') || action.includes('import') || action.includes('reveal') || action.includes('unmask')) return 'magenta';
  return 'default';
}
